import {
    ScheduleMilestoneAlreadyRecognizedError,
    SchedulePeriodLockedError,
} from "@api/scheduleErrors";
import { getApiErrorMessage } from "@api/apiError";
import { pluralizeWithCount } from "@common/helpers/string";
import { formatOverRecognizedSuffix } from "@components/accounting/schedules/applications/milestoneAchievedCopy";
import { MilestoneReviewRow } from "./milestoneReviewHelpers";

export interface MilestoneConfirmOutcome {
    confirmedPeriodIds: string[];
    overRecognizedCount: number;
    alreadyPostedPeriodIds: string[];
    lockedError: SchedulePeriodLockedError | null;
    lockedCount: number;
    firstFailure: string | null;
    failedCount: number;
}

export async function confirmMilestoneRows(options: {
    rows: readonly MilestoneReviewRow[];
    recognize: (
        row: MilestoneReviewRow,
    ) => Promise<{ isOverRecognized: boolean }>;
}): Promise<MilestoneConfirmOutcome> {
    const outcome: MilestoneConfirmOutcome = {
        confirmedPeriodIds: [],
        overRecognizedCount: 0,
        alreadyPostedPeriodIds: [],
        lockedError: null,
        lockedCount: 0,
        firstFailure: null,
        failedCount: 0,
    };
    for (const row of options.rows) {
        try {
            const result = await options.recognize(row);
            outcome.confirmedPeriodIds.push(row.periodId);
            if (result.isOverRecognized) {
                outcome.overRecognizedCount += 1;
            }
        } catch (error) {
            if (error instanceof ScheduleMilestoneAlreadyRecognizedError) {
                outcome.alreadyPostedPeriodIds.push(row.periodId);
                continue;
            }
            if (error instanceof SchedulePeriodLockedError) {
                outcome.lockedError ??= error;
                outcome.lockedCount += 1;
                continue;
            }
            outcome.firstFailure ??= getApiErrorMessage(
                error,
                `Failed to recognize ${row.name}`,
            );
            outcome.failedCount += 1;
        }
    }
    return outcome;
}

/**
 * The toast for a confirm round: what posted, or that every row was already
 * recognized elsewhere. Null when a lock or failure explains the round instead.
 */
export function formatMilestoneConfirmToast(options: {
    outcome: Pick<
        MilestoneConfirmOutcome,
        | "confirmedPeriodIds"
        | "overRecognizedCount"
        | "alreadyPostedPeriodIds"
        | "lockedCount"
        | "failedCount"
    >;
    /** Null when the round spans schedules. */
    sourceLabel: string | null;
}): string | null {
    const { outcome } = options;
    const forSource =
        options.sourceLabel === null ? "" : ` for ${options.sourceLabel}`;
    const confirmedCount = outcome.confirmedPeriodIds.length;
    const skippedCount = outcome.lockedCount + outcome.failedCount;
    const alreadyPostedCount = outcome.alreadyPostedPeriodIds.length;
    if (confirmedCount === 0) {
        return alreadyPostedCount > 0 && skippedCount === 0
            ? `${pluralizeWithCount("milestone", alreadyPostedCount)} already recognized${forSource}`
            : null;
    }
    const recognized = `Recognized ${pluralizeWithCount("milestone", confirmedCount)}${forSource}`;
    const withSkipped =
        skippedCount > 0 ? `${recognized}, ${skippedCount} failed` : recognized;
    return outcome.overRecognizedCount > 0
        ? `${withSkipped}. ${formatOverRecognizedSuffix(outcome.overRecognizedCount)}`
        : withSkipped;
}
